import { useEffect, useState } from 'react';
import { Alert, Button, IconButton, InputAdornment, TextField, Tooltip } from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ForwardToInboxIcon from '@mui/icons-material/ForwardToInbox';
import LinkIcon from '@mui/icons-material/Link';
import { AdminUser } from '../../common/Rest/Services/AdminService/AdminService';
import { AccountInvite, createAccountInvite } from '../../common/Rest/Services/AccountInviteService';
import { ApiError } from '../../common/Rest/ReaderlyHttpClient';

const isTechnicalEmail = (email: string) => !email || email.toLowerCase().endsWith('@readerly.local');

const formatExpiry = (value: string) =>
  new Date(value).toLocaleString('uk-UA', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });

interface AccountInviteBoxProps {
  user: AdminUser;
}

/** Lets the admin hand an account they created over to the actual client via a one-time link. */
function AccountInviteBox({ user }: AccountInviteBoxProps) {
  const [invite, setInvite] = useState<AccountInvite | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [copied, setCopied] = useState(false);
  const canSendEmail = !isTechnicalEmail(user.email);

  useEffect(() => {
    setInvite(null);
    setError(null);
    setCopied(false);
  }, [user.id]);

  if (user.hasLogin) return null;

  const handleCreate = async (sendEmail: boolean) => {
    setCreating(true);
    setError(null);
    setCopied(false);
    try {
      setInvite(await createAccountInvite(user.id, sendEmail));
    } catch (err) {
      setError(err instanceof ApiError && err.status === 409
        ? 'У цього користувача вже є вхід на сайт.'
        : 'Не вдалося створити запрошення.');
    } finally {
      setCreating(false);
    }
  };

  const handleCopy = async () => {
    if (!invite) return;
    try {
      await navigator.clipboard.writeText(invite.link);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  };

  return (
    <section className="admin-book-images admin-invite">
      <h3>Запросити на сайт</h3>
      <p className="admin-invite-hint">
        Цей акаунт створено вручну, і клієнт поки не може в нього увійти. Надішліть йому посилання — він вкаже свою
        пошту, телефон і пароль, і акаунт разом з історією замовлень стане його. Посилання діє 7 днів і спрацьовує один раз.
      </p>

      <div className="admin-actions">
        <Button variant="contained" startIcon={<LinkIcon />} onClick={() => handleCreate(false)} disabled={creating}>
          {invite ? 'Створити нове посилання' : 'Створити посилання'}
        </Button>
        {canSendEmail && (
          <Button variant="outlined" startIcon={<ForwardToInboxIcon />} onClick={() => handleCreate(true)} disabled={creating}>
            Надіслати на {user.email}
          </Button>
        )}
      </div>

      {error && <Alert severity="error">{error}</Alert>}

      {invite && (
        <>
          <TextField
            label="Посилання для клієнта"
            value={invite.link}
            fullWidth
            onFocus={(event) => event.target.select()}
            slotProps={{
              input: {
                readOnly: true,
                endAdornment: (
                  <InputAdornment position="end">
                    <Tooltip title={copied ? 'Скопійовано!' : 'Скопіювати'}>
                      <IconButton aria-label="Скопіювати посилання" onClick={handleCopy} edge="end">
                        <ContentCopyIcon />
                      </IconButton>
                    </Tooltip>
                  </InputAdornment>
                ),
              },
            }}
            helperText={`Діє до ${formatExpiry(invite.expiresAt)}. Попередні посилання для цього клієнта більше не працюють.`}
          />
          {invite.emailSent && <Alert severity="success">Лист із посиланням надіслано на {user.email}.</Alert>}
          {invite.emailError && <Alert severity="warning">{invite.emailError}</Alert>}
        </>
      )}
    </section>
  );
}

export default AccountInviteBox;
